import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { pedidosApi, productosApi, entidadesApi, resolveAssetUrl } from '../services/api';
import {
  ArrowLeft, Loader2, CheckCircle2, AlertTriangle, FileText, Sparkles, Trash2,
  Plus, Save, ExternalLink, Download, PackageCheck,
} from 'lucide-react';

const ESTADO_PEDIDO_META = {
  ocr_procesando:  { label: 'OCR en curso',   color: 'bg-amber-100 text-amber-700 border-amber-200' },
  revision_humana: { label: 'Revisión',       color: 'bg-blue-100 text-blue-700 border-blue-200' },
  confirmado:      { label: 'Confirmado',     color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  enviado_erp:     { label: 'Enviado ERP',    color: 'bg-violet-100 text-violet-700 border-violet-200' },
  error_ocr:       { label: 'Error OCR',      color: 'bg-rose-100 text-rose-700 border-rose-200' },
  archivado:       { label: 'Archivado',      color: 'bg-ink-100 text-ink-600 border-ink-200' },
};

const ESTADO_LINEA_META = {
  auto_ok:       { label: 'Resuelta',       color: 'text-emerald-700 bg-emerald-50 border-emerald-200', icon: CheckCircle2 },
  auto_mapeo:    { label: 'Aprendida',      color: 'text-cyan-700 bg-cyan-50 border-cyan-200', icon: Sparkles },
  sugerido:      { label: 'Sugerida',       color: 'text-amber-700 bg-amber-50 border-amber-200', icon: AlertTriangle },
  no_encontrado: { label: 'Por revisar',    color: 'text-rose-700 bg-rose-50 border-rose-200', icon: AlertTriangle },
  manual:        { label: 'Manual',         color: 'text-violet-700 bg-violet-50 border-violet-200', icon: CheckCircle2 },
};

// Buscador de producto por referencia/descripción (lista simple con input)
const ProductoPicker = ({ value, descripcionActual, onPick, testidPrefix }) => {
  const [q, setQ] = useState('');
  const [productos, setProductos] = useState([]);
  const [open, setOpen] = useState(false);

  const buscar = useCallback(async (term) => {
    if (!term || term.length < 2) { setProductos([]); return; }
    try {
      const list = await productosApi.list({ q: term, limit: 10 });
      setProductos(list);
    } catch { /* silencioso */ }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => buscar(q), 250);
    return () => clearTimeout(t);
  }, [q, buscar]);

  return (
    <div className="relative">
      <Input
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder={value ? `Actual: ${value}` : descripcionActual ? `Buscar (ej: ${descripcionActual.slice(0, 30)})` : 'Buscar producto…'}
        className="text-xs"
        data-testid={`${testidPrefix}-input`}
      />
      {open && productos.length > 0 && (
        <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded-md shadow-lg mt-1 max-h-72 overflow-y-auto">
          {productos.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => { onPick(p); setOpen(false); setQ(''); }}
              className="w-full text-left px-3 py-2 hover:bg-brand-50 text-xs border-b border-ink-100 last:border-0"
              data-testid={`${testidPrefix}-opt-${p.id}`}
            >
              <div className="font-medium text-ink-900">{p.referencia}</div>
              <div className="text-ink-500 truncate">{p.descripcion}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default function PedidoDetalle() {
  const { id } = useParams();
  const nav = useNavigate();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [confirmando, setConfirmando] = useState(false);
  const [propuesta, setPropuesta] = useState(null);
  const [buscarEntidad, setBuscarEntidad] = useState('');
  const [entidadOpts, setEntidadOpts] = useState([]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const ped = await pedidosApi.get(id);
      setP(ped);
    } catch (e) {
      toast.error(e.message);
      nav('/pedidos');
    } finally {
      setLoading(false);
    }
  }, [id, nav]);

  useEffect(() => { cargar(); }, [cargar]);

  useEffect(() => {
    if (!buscarEntidad || buscarEntidad.length < 2) { setEntidadOpts([]); return; }
    const t = setTimeout(async () => {
      try {
        const res = await entidadesApi.autocomplete(buscarEntidad);
        setEntidadOpts(res || []);
      } catch { /* noop */ }
    }, 300);
    return () => clearTimeout(t);
  }, [buscarEntidad]);

  const patchLinea = async (lid, patch) => {
    setSaving((s) => ({ ...s, [lid]: true }));
    try {
      const upd = await pedidosApi.updateLinea(id, lid, patch);
      setP(upd);
      toast.success('Línea actualizada — mapeo aprendido para la próxima vez');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving((s) => ({ ...s, [lid]: false }));
    }
  };

  const borrarLinea = async (lid) => {
    if (!window.confirm('¿Eliminar esta línea?')) return;
    try {
      const upd = await pedidosApi.deleteLinea(id, lid);
      setP(upd);
      toast.success('Línea eliminada');
    } catch (e) {
      toast.error(e.message);
    }
  };

  const anadirLinea = async () => {
    try {
      const upd = await pedidosApi.addLinea(id, {
        descripcion_cliente: 'Nueva línea',
        cantidad: 1,
      });
      setP(upd);
    } catch (e) { toast.error(e.message); }
  };

  const asignarEntidad = async (ent) => {
    try {
      const upd = await pedidosApi.updateHeader(id, { entidad_id: ent.id });
      setP({ ...upd, entidad_razon_social: ent.razon_social });
      toast.success(`Entidad asignada: ${ent.razon_social}`);
      setBuscarEntidad('');
      setEntidadOpts([]);
    } catch (e) { toast.error(e.message); }
  };

  const confirmar = async () => {
    setConfirmando(true);
    try {
      const prop = await pedidosApi.confirmar(id);
      setPropuesta(prop);
      toast.success(`Propuesta generada · ID ERP stub: ${prop.lineas_finales?.length} líneas`);
      await cargar();
    } catch (e) {
      const msg = e.message;
      toast.error(msg);
    } finally {
      setConfirmando(false);
    }
  };

  if (loading || !p) {
    return (
      <div className="p-10 text-center text-ink-500">
        <Loader2 className="w-5 h-5 animate-spin inline mr-2" />
        Cargando pedido…
      </div>
    );
  }

  const meta = ESTADO_PEDIDO_META[p.estado] || { label: p.estado, color: 'bg-ink-100 text-ink-700' };
  const confirmable = ['revision_humana'].includes(p.estado) && p.lineas?.length > 0 && p.lineas.every((l) => l.producto_id);
  const docUrl = p.documento_origen_url ? resolveAssetUrl(p.documento_origen_url) : null;

  return (
    <div className="space-y-6" data-testid="pedido-detalle">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <Button variant="ghost" size="sm" onClick={() => nav('/pedidos')} data-testid="btn-volver">
            <ArrowLeft className="w-4 h-4 mr-1" /> Pedidos
          </Button>
          <h1 className="text-3xl font-bold text-ink-900 font-display tracking-tight mt-1">
            {p.numero_pedido_cliente || 'Pedido sin número'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Badge variant="outline" className={`text-xs ${meta.color}`}>{meta.label}</Badge>
            {p.fecha_pedido && <span className="text-xs text-ink-500">Fecha: {p.fecha_pedido}</span>}
            {p.enviado_erp_id && (
              <span className="text-xs text-violet-700 font-mono bg-violet-50 px-2 py-0.5 rounded border border-violet-200">
                {p.enviado_erp_id}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {docUrl && (
            <Button variant="outline" asChild data-testid="btn-ver-original">
              <a href={docUrl} target="_blank" rel="noreferrer">
                <ExternalLink className="w-4 h-4 mr-2" /> Ver original
              </a>
            </Button>
          )}
          <Button
            onClick={confirmar}
            disabled={!confirmable || confirmando}
            data-testid="btn-confirmar-pedido"
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {confirmando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-2" />}
            Confirmar y generar oferta
          </Button>
        </div>
      </div>

      {/* Cliente */}
      <Card className="p-4">
        <div className="text-xs font-semibold text-ink-600 uppercase mb-2">Cliente</div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex-1 min-w-[240px]">
            <div className="font-medium text-ink-900" data-testid="pedido-entidad-nombre">
              {p.entidad_razon_social || <span className="text-rose-600">Sin cliente asignado</span>}
            </div>
            {p.cif_detectado && <div className="text-xs text-ink-500">CIF detectado: {p.cif_detectado}</div>}
            {p.entidad_id && <div className="text-xs text-emerald-700">✓ Vinculado a empresa CRM</div>}
            {!p.entidad_id && p.entidad_razon_social && (
              <div className="text-xs text-amber-700 mt-1">⚠ Sin vincular — busca abajo o crea la empresa desde /clientes</div>
            )}
          </div>
          {!p.entidad_id && p.estado !== 'enviado_erp' && (
            <div className="relative w-full sm:w-80">
              <Input
                value={buscarEntidad}
                onChange={(e) => setBuscarEntidad(e.target.value)}
                placeholder="Buscar empresa CRM para vincular…"
                data-testid="pedido-buscar-entidad"
              />
              {entidadOpts.length > 0 && (
                <div className="absolute top-full left-0 right-0 z-[9999] bg-white border border-ink-200 rounded-md shadow-lg mt-1 max-h-64 overflow-y-auto">
                  {entidadOpts.map((ent) => (
                    <button
                      key={ent.id}
                      type="button"
                      onClick={() => asignarEntidad(ent)}
                      className="w-full text-left px-3 py-2 hover:bg-brand-50 text-xs border-b border-ink-100 last:border-0"
                      data-testid={`pedido-ent-opt-${ent.id}`}
                    >
                      <div className="font-medium text-ink-900">{ent.razon_social}</div>
                      <div className="text-ink-500">{ent.cif || ''} · {ent.provincia || ''}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </Card>

      {p.error_ocr && (
        <Card className="p-4 bg-rose-50 border-rose-200">
          <div className="flex items-start gap-2 text-sm text-rose-800">
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Error durante el OCR</div>
              <div className="text-xs mt-1 font-mono">{p.error_ocr}</div>
              <div className="text-xs mt-2">Puedes añadir líneas manualmente abajo o re-subir el documento desde la lista.</div>
            </div>
          </div>
        </Card>
      )}

      {/* Líneas */}
      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink-100">
          <div className="font-semibold text-ink-800 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Líneas del pedido
          </div>
          {p.estado !== 'enviado_erp' && (
            <Button size="sm" variant="outline" onClick={anadirLinea} data-testid="btn-add-linea">
              <Plus className="w-4 h-4 mr-1" /> Añadir línea
            </Button>
          )}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="lineas-tabla">
            <thead className="bg-ink-50 text-ink-600 uppercase text-xs">
              <tr>
                <th className="text-left px-3 py-2">Estado</th>
                <th className="text-left px-3 py-2">Referencia cliente</th>
                <th className="text-left px-3 py-2">Descripción cliente</th>
                <th className="text-right px-3 py-2">Cant.</th>
                <th className="text-left px-3 py-2">Producto CRM asignado</th>
                <th className="text-left px-3 py-2">Buscar / cambiar</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(p.lineas || []).map((l) => {
                const m = ESTADO_LINEA_META[l.estado] || ESTADO_LINEA_META.no_encontrado;
                const Icon = m.icon;
                return (
                  <tr key={l.id} className="border-t border-ink-100" data-testid={`linea-row-${l.id}`}>
                    <td className="px-3 py-3">
                      <Badge variant="outline" className={`text-xs ${m.color}`}>
                        <Icon className="w-3 h-3 inline mr-1" />
                        {m.label}
                      </Badge>
                      {l.confianza > 0 && (
                        <div className="text-[10px] text-ink-400 mt-1">conf. {Math.round(l.confianza * 100)}%</div>
                      )}
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-ink-700">{l.referencia_cliente || <span className="text-ink-300">—</span>}</td>
                    <td className="px-3 py-3 text-ink-700">{l.descripcion_cliente || <span className="text-ink-300 italic">sin descripción</span>}</td>
                    <td className="px-3 py-3 text-right text-ink-700">{l.cantidad ?? '—'} {l.unidad || ''}</td>
                    <td className="px-3 py-3">
                      {l.producto_id ? (
                        <>
                          <div className="font-mono text-xs font-semibold text-emerald-700">{l.producto_referencia}</div>
                          <div className="text-xs text-ink-500 truncate max-w-[240px]">{l.producto_descripcion}</div>
                        </>
                      ) : (
                        <span className="text-xs text-rose-500">sin asignar</span>
                      )}
                    </td>
                    <td className="px-3 py-3 w-64">
                      {p.estado !== 'enviado_erp' && (
                        <ProductoPicker
                          value={l.producto_referencia}
                          descripcionActual={l.descripcion_cliente}
                          onPick={(prod) => patchLinea(l.id, { producto_id: prod.id })}
                          testidPrefix={`picker-${l.id}`}
                        />
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      {p.estado !== 'enviado_erp' && (
                        <Button size="sm" variant="ghost" onClick={() => borrarLinea(l.id)} data-testid={`btn-del-linea-${l.id}`} disabled={saving[l.id]}>
                          <Trash2 className="w-4 h-4 text-rose-500" />
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {(p.lineas || []).length === 0 && (
          <div className="p-8 text-center text-ink-400 text-sm">Sin líneas — añade la primera con el botón "Añadir línea"</div>
        )}
      </Card>

      {/* Propuesta final tras confirmar */}
      {propuesta && (
        <Card className="p-4 bg-emerald-50/60 border-emerald-200" data-testid="propuesta-final">
          <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-3">
            <Sparkles className="w-4 h-4" />
            Propuesta de oferta generada
          </div>
          <div className="text-xs text-ink-600 mb-3">
            Copia estas líneas en tu ERP (referencia CRM + cantidad) para cerrar la operación.
          </div>
          <div className="overflow-x-auto bg-white rounded border border-ink-200">
            <table className="w-full text-sm">
              <thead className="bg-ink-50 text-xs uppercase text-ink-600">
                <tr>
                  <th className="text-left px-3 py-2">Ref. CRM</th>
                  <th className="text-left px-3 py-2">Descripción</th>
                  <th className="text-left px-3 py-2">Ref. cliente</th>
                  <th className="text-right px-3 py-2">Cantidad</th>
                  <th className="text-right px-3 py-2">Precio €</th>
                </tr>
              </thead>
              <tbody>
                {propuesta.lineas_finales.map((l, i) => (
                  <tr key={i} className="border-t border-ink-100">
                    <td className="px-3 py-2 font-mono text-xs font-semibold text-emerald-700">{l.referencia_crm}</td>
                    <td className="px-3 py-2 text-ink-700 text-xs">{l.descripcion_crm}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-500">{l.referencia_cliente || '—'}</td>
                    <td className="px-3 py-2 text-right">{l.cantidad} {l.unidad || ''}</td>
                    <td className="px-3 py-2 text-right">{l.precio_cliente != null ? l.precio_cliente.toFixed(2) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            onClick={() => {
              const txt = propuesta.lineas_finales
                .map((l) => `${l.referencia_crm}\t${l.cantidad}\t${l.descripcion_crm || ''}`)
                .join('\n');
              navigator.clipboard.writeText(txt);
              toast.success('Copiado al portapapeles (TSV)');
            }}
            data-testid="btn-copiar-propuesta"
          >
            <Download className="w-4 h-4 mr-2" />
            Copiar (TSV)
          </Button>
        </Card>
      )}
    </div>
  );
}
